import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, PhoneCall, Filter, Eye, Send, X, Download, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { chatbotAdminService } from '../../../services/chatbotService';
import { agentService } from '../../../services/agentService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const STATUS_COLORS = {
  SATISFIED:          'bg-green-100 text-green-700',
  ESCALATED:          'bg-yellow-100 text-yellow-700',
  UNRESOLVED:         'bg-red-100 text-red-700',
  CALLBACK_REQUESTED: 'bg-blue-100 text-blue-700',
};

const STATUS_ICONS = {
  SATISFIED:          <CheckCircle className="w-4 h-4" />,
  ESCALATED:          <AlertTriangle className="w-4 h-4" />,
  UNRESOLVED:         <XCircle className="w-4 h-4" />,
  CALLBACK_REQUESTED: <PhoneCall className="w-4 h-4" />,
};

const MSG_BUBBLE = {
  user:      'bg-gray-100 text-gray-800 self-start',
  assistant: 'bg-green-50 text-green-800 italic self-end',
  agent:     'bg-blue-100 text-blue-900 self-end',
};

const AgentAnalytics = () => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const isAdmin  = !localStorage.getItem('agentRole');
  const companyId = localStorage.getItem('companyId');

  const [summary, setSummary]             = useState(null);
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents]               = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef(null);
  const [page, setPage]                   = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');

  // Detail modal state
  const [detailConv, setDetailConv]       = useState(null);
  const [messages, setMessages]           = useState([]);
  const [loadingMsgs, setLoadingMsgs]     = useState(false);
  const [updateNote, setUpdateNote]       = useState('');
  const [closeNote, setCloseNote]         = useState('');
  const [closeStatus, setCloseStatus]     = useState('SATISFIED');
  const [submitting, setSubmitting]       = useState(false);
  const [detailError, setDetailError]     = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const SIZE = 20;

  const fetchSummary = useCallback(async () => {
    try {
      const data = isAdmin
        ? await chatbotAdminService.getCompanySummary(companyId)
        : await agentService.getMySummary();
      setSummary(data);
    } catch { /* non-critical */ }
  }, [isAdmin, companyId]);

  const fetchConversations = useCallback(async (pageNum = 0) => {
    setLoading(true);
    setError('');
    try {
      const data = isAdmin
        ? await chatbotAdminService.getCompanyHandledConversations(
            companyId, pageNum, SIZE, selectedAgent || null, selectedStatus || null, search || null)
        : await agentService.getMyHandledConversations(pageNum, SIZE, selectedStatus || null, search || null);
      setConversations(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(pageNum);
    } catch {
      setError(t('agentAnalytics.loadError'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, companyId, selectedAgent, selectedStatus, search, t]);

  const fetchAgentsList = useCallback(async () => {
    if (!isAdmin || !companyId) return;
    try {
      const data = await chatbotAdminService.getAgentsList(companyId);
      setAgents(data || []);
    } catch { /* non-critical */ }
  }, [isAdmin, companyId]);

  useEffect(() => { fetchSummary(); fetchAgentsList(); }, []);
  useEffect(() => { fetchConversations(0); }, [selectedAgent, selectedStatus, search, fetchConversations]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearch(val), 400);
  };

  const svc = {
    getMessages: (sid) => isAdmin
      ? chatbotAdminService.getConversationMessages(sid)
      : agentService.getConversationMessages(sid),
    addUpdate: (sid, note) => isAdmin
      ? chatbotAdminService.addCallbackUpdate(sid, note)
      : agentService.addCallbackUpdate(sid, note),
    closeCallback: (sid, note, status) => isAdmin
      ? chatbotAdminService.closeCallbackConversation(sid, note, status)
      : agentService.closeCallbackConversation(sid, note, status),
  };

  const openDetail = async (conv) => {
    setDetailConv(conv);
    setMessages([]);
    setUpdateNote('');
    setCloseNote('');
    setCloseStatus('SATISFIED');
    setDetailError('');
    setShowCloseConfirm(false);
    setLoadingMsgs(true);
    try {
      const msgs = await svc.getMessages(conv.sessionId);
      setMessages(msgs || []);
    } catch {
      setDetailError(t('agentAnalytics.messagesLoadError'));
    } finally {
      setLoadingMsgs(false);
    }
  };

  const closeDetail = () => {
    setDetailConv(null);
    setMessages([]);
    setUpdateNote('');
    setCloseNote('');
    setCloseStatus('SATISFIED');
    setDetailError('');
    setShowCloseConfirm(false);
  };

  const handleAddUpdate = async () => {
    if (!updateNote.trim()) return;
    setSubmitting(true);
    setDetailError('');
    try {
      await svc.addUpdate(detailConv.sessionId, updateNote.trim());
      const msgs = await svc.getMessages(detailConv.sessionId);
      setMessages(msgs || []);
      setUpdateNote('');
    } catch (e) {
      setDetailError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseCallback = async () => {
    setSubmitting(true);
    setDetailError('');
    try {
      await svc.closeCallback(detailConv.sessionId, closeNote.trim(), closeStatus);
      const updated = { ...detailConv, resolutionStatus: closeStatus };
      setDetailConv(updated);
      setConversations(prev => prev.map(c =>
        c.sessionId === detailConv.sessionId ? updated : c
      ));
      fetchSummary();
      setShowCloseConfirm(false);
      setCloseNote('');
      const msgs = await svc.getMessages(detailConv.sessionId);
      setMessages(msgs || []);
    } catch (e) {
      setDetailError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const summaryCards = summary ? [
    { label: t('agentAnalytics.totalHandled'),      value: summary.totalHandled,          color: 'text-gray-900' },
    { label: t('agentAnalytics.satisfied'),         value: summary.satisfiedCount,         color: 'text-green-600' },
    { label: t('agentAnalytics.escalated'),         value: summary.escalatedCount,         color: 'text-yellow-600' },
    { label: t('agentAnalytics.unresolved'),        value: summary.unresolvedCount,        color: 'text-red-600' },
    { label: t('agentAnalytics.callbackRequested'), value: summary.callbackRequestedCount, color: 'text-blue-600' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('agentAnalytics.title')}</h1>
        <p className="text-gray-600 mt-1">{t('agentAnalytics.subtitle')}</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className={`text-2xl font-bold ${card.color}`}>
                {card.value?.toLocaleString() ?? '—'}
              </div>
              <div className="text-sm text-gray-600 mt-1">{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-4 h-4 text-gray-400" />

          {/* Search by visitor name / email */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder={t('agentAnalytics.searchPlaceholder')}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setSearch(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {isAdmin && (
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">{t('agentAnalytics.allAgents')}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.fullName}</option>
              ))}
            </select>
          )}

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">{t('agentAnalytics.allStatuses')}</option>
            <option value="SATISFIED">{t('agentAnalytics.resolution_satisfied')}</option>
            <option value="ESCALATED">{t('agentAnalytics.resolution_escalated')}</option>
            <option value="UNRESOLVED">{t('agentAnalytics.resolution_unresolved')}</option>
            <option value="CALLBACK_REQUESTED">{t('agentAnalytics.resolution_callback_requested')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('agentAnalytics.handledConversations')}</h2>

        {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{t('agentAnalytics.noConversations')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-3 pr-4">{t('agentAnalytics.visitor')}</th>
                  {isAdmin && <th className="pb-3 pr-4">{t('agentAnalytics.agent')}</th>}
                  <th className="pb-3 pr-4">{t('agentAnalytics.messages')}</th>
                  <th className="pb-3 pr-4">{t('agentAnalytics.status')}</th>
                  <th className="pb-3 pr-4">{t('agentAnalytics.note')}</th>
                  <th className="pb-3 pr-4">{t('agentAnalytics.resolvedAt')}</th>
                  <th className="pb-3">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {conversations.map((conv) => (
                  <tr key={conv.sessionId} className="hover:bg-gray-50">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900">{conv.visitorName || '—'}</div>
                      <div className="text-xs text-gray-500">{conv.visitorEmail || ''}</div>
                    </td>
                    {isAdmin && (
                      <td className="py-3 pr-4 text-gray-700">{conv.agentName || '—'}</td>
                    )}
                    <td className="py-3 pr-4 text-gray-700">{conv.messageCount}</td>
                    <td className="py-3 pr-4">
                      {conv.resolutionStatus ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[conv.resolutionStatus] || 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_ICONS[conv.resolutionStatus]}
                          {t(`agentAnalytics.resolution_${conv.resolutionStatus.toLowerCase()}`)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-600 max-w-xs truncate">
                      {conv.resolutionNote || '—'}
                    </td>
                    <td className="py-3 pr-4 text-gray-500 text-xs">
                      {conv.resolvedAt ? formatDateTime(conv.resolvedAt) : '—'}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => openDetail(conv)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {t('common.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {t('agentAnalytics.totalConversations', { count: totalElements.toLocaleString() })}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchConversations(page - 1)}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 text-sm"
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {t('activityLog.pageOf', { current: page + 1, total: totalPages })}
              </span>
              <button
                onClick={() => fetchConversations(page + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 text-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {detailConv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {detailConv.visitorName || t('chatbot.conv_anonymous')}
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  {detailConv.resolutionStatus && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[detailConv.resolutionStatus] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_ICONS[detailConv.resolutionStatus]}
                      {t(`agentAnalytics.resolution_${detailConv.resolutionStatus.toLowerCase()}`)}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">{detailConv.sessionId}</span>
                </div>
              </div>
              <button onClick={closeDetail} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {loadingMsgs ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">{t('agentAnalytics.noMessages')}</p>
              ) : (
                messages.map((msg, idx) => {
                  const role = (msg.role || '').toLowerCase();
                  const isUser = role === 'user';
                  const label = isUser
                    ? (detailConv.visitorName || t('chatbot.conv_visitor'))
                    : role === 'agent'
                    ? (msg.agentName || t('chatbot.conv_agent'))
                    : t('chatbot.conv_bot');

                  return (
                    <div key={msg.id ?? idx} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${
                        isUser ? 'bg-gray-100 text-gray-800' :
                        role === 'agent' ? 'bg-blue-100 text-blue-900' :
                        'bg-green-50 text-green-800 italic'
                      }`}>
                        <p className="text-xs font-semibold mb-1 opacity-60">{label}</p>
                        {msg.mediaUrl && (
                          <div className="mb-1">
                            <img
                              src={msg.mediaUrl}
                              alt="attachment"
                              className="rounded-lg max-w-full mb-1 object-contain"
                              style={{ maxHeight: 180 }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                            <a
                              href={msg.mediaUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Download className="w-3 h-3" />
                              {t('common.download')}
                            </a>
                          </div>
                        )}
                        {msg.content && <p className="leading-relaxed">{msg.content}</p>}
                        {msg.createdAt && (
                          <p className="text-xs mt-1 opacity-40">
                            {formatDateTime(msg.createdAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Callback actions — only for CALLBACK_REQUESTED */}
            {detailConv.resolutionStatus === 'CALLBACK_REQUESTED' && (
              <div className="px-6 py-4 border-t border-gray-200 space-y-3">
                {detailError && (
                  <p className="text-red-600 text-xs">{detailError}</p>
                )}

                {/* Add update note */}
                {!showCloseConfirm && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">
                      {t('agentAnalytics.addCallbackUpdate')}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={updateNote}
                        onChange={e => setUpdateNote(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !submitting && handleAddUpdate()}
                        placeholder={t('agentAnalytics.updateNotePlaceholder')}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        onClick={handleAddUpdate}
                        disabled={submitting || !updateNote.trim()}
                        className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {t('agentAnalytics.addUpdate')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Close conversation */}
                {!showCloseConfirm ? (
                  <button
                    onClick={() => setShowCloseConfirm(true)}
                    className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-1.5"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t('agentAnalytics.closeConversation')}
                  </button>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-semibold text-gray-800">{t('agentAnalytics.closeConfirmTitle')}</p>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">{t('agentAnalytics.closingStatus')}</label>
                      <select
                        value={closeStatus}
                        onChange={e => setCloseStatus(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="SATISFIED">{t('agentAnalytics.resolution_satisfied')}</option>
                        <option value="ESCALATED">{t('agentAnalytics.resolution_escalated')}</option>
                        <option value="UNRESOLVED">{t('agentAnalytics.resolution_unresolved')}</option>
                      </select>
                    </div>
                    <textarea
                      value={closeNote}
                      onChange={e => setCloseNote(e.target.value)}
                      rows={2}
                      placeholder={t('agentAnalytics.closeNotePlaceholder')}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowCloseConfirm(false)}
                        className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleCloseCallback}
                        disabled={submitting}
                        className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {submitting ? t('common.saving') : t('agentAnalytics.confirmClose')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentAnalytics;
